import React from 'react';
import type { ClosedPnL } from '../../lib/types';
import { formatCurrency, formatDateDDMMYYYY, calculateTimeInMarket } from './formatters';

interface ClosedTradesTableProps {
  trades: Array<ClosedPnL & { userName: string }>;
}

const ClosedPnLRow: React.FC<{ trade: ClosedPnL & { userName: string } }> = ({ trade }) => {
  const pnl = Number.parseFloat(trade.closedPnl || '0');
  const isProfit = pnl > 0;
  const entryPrice = Number.parseFloat(trade.avgEntryPrice || '0');
  const exitPrice = Number.parseFloat(trade.avgExitPrice || '0');
  const qty = Number.parseFloat(trade.qty || '0');
  const openVolume = Number.parseFloat(trade.cumEntryValue || '0');
  const closeVolume = Number.parseFloat(trade.cumExitValue || '0');
  const openingFee = Number.parseFloat(trade.openingFee || '0');
  const closingFee = Number.parseFloat(trade.closingFee || '0');

  return (
    <tr className="border-b border-gray-200 hover:bg-gray-50">
      <td className="px-4 py-3 text-sm text-gray-900">{trade.userName}</td>
      <td className="px-4 py-3 text-sm text-gray-900">{trade.symbol}</td>
      <td className="px-4 py-3 text-sm">
        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${trade.side === 'Buy' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
          {trade.side}
        </span>
      </td>
      <td className="px-4 py-3 text-sm text-gray-900">{qty.toFixed(4)}</td>
      <td className="px-4 py-3 text-sm text-gray-900">{formatCurrency(entryPrice)}</td>
      <td className="px-4 py-3 text-sm text-gray-900">{formatCurrency(exitPrice)}</td>
      <td className="px-4 py-3 text-sm text-gray-900">{trade.orderType || trade.execType || '-'}</td>
      <td className="px-4 py-3 text-sm">
        <span className={`font-semibold ${isProfit ? 'text-green-600' : 'text-red-600'}`}>{formatCurrency(pnl)}</span>
      </td>
      <td className="px-4 py-3 text-sm">
        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${isProfit ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
          {isProfit ? 'Win' : 'Loss'}
        </span>
      </td>
      <td className="px-4 py-3 text-sm text-gray-900">{formatCurrency(openVolume)}</td>
      <td className="px-4 py-3 text-sm text-gray-900">{formatCurrency(closeVolume)}</td>
      <td className="px-4 py-3 text-sm text-gray-900">{openingFee > 0 ? formatCurrency(openingFee) : '-'}</td>
      <td className="px-4 py-3 text-sm text-gray-900">{closingFee > 0 ? formatCurrency(closingFee) : '-'}</td>
      <td className="px-4 py-3 text-sm text-gray-900">{trade.leverage}x</td>
      <td className="px-4 py-3 text-sm text-gray-600">{calculateTimeInMarket(trade.createdTime, trade.updatedTime)}</td>
      <td className="px-4 py-3 text-sm text-gray-600">{formatDateDDMMYYYY(trade.createdTime)}</td>
      <td className="px-4 py-3 text-sm text-gray-600">{formatDateDDMMYYYY(trade.updatedTime)}</td>
    </tr>
  );
};

export const ClosedTradesTable: React.FC<ClosedTradesTableProps> = ({ trades }) => {
  if (trades.length === 0) {
    return null;
  }

  return (
    <div className="bg-white shadow rounded-lg">
      <div className="px-6 py-4 border-b border-gray-200">
        <h2 className="text-lg font-semibold text-gray-900">Closed Trades with P&L</h2>
        <p className="text-sm text-gray-600 mt-1">Completed trades with final profit/loss</p>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Trader</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Symbol</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Side</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Qty</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Entry Price</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Exit Price</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">P&L</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Outcome</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Open Volume</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Close Volume</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Opening Fee</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Closing Fee</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Leverage</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Time in Market</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Closed</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {trades
              .sort((a, b) => Number.parseInt(b.updatedTime, 10) - Number.parseInt(a.updatedTime, 10))
              .slice(0, 50)
              .map((trade, index) => (
                <ClosedPnLRow key={`${trade.orderId}-${index}`} trade={trade} />
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
